import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useSite } from "../../context/SiteContext";
import HeroMedia from "../HeroMedia";
import FieldLines from "../FieldLines";
import { PlusButton } from "../PlusButton";

const EASE = [0.16, 1, 0.3, 1];

export default function Hero() {
    const { site, tx } = useSite();
    const hero = site?.hero || {};
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const mediaScale = useTransform(scrollYProgress, [0, 1], [1, 1.18]);
    const textY = useTransform(scrollYProgress, [0, 1], [0, 160]);
    const textScale = useTransform(scrollYProgress, [0, 1], [1, 0.92]);
    const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);
    const linesY = useTransform(scrollYProgress, [0, 1], [0, -220]);
    const plusY = useTransform(scrollYProgress, [0, 1], [0, -340]);

    const h2 = tx(hero.headline_2) || "THE FIELD+";
    const h2Main = h2.replace("+", "");

    return (
        <section ref={ref} id="hero" data-testid="hero-section" className="relative h-[100svh] overflow-hidden bg-[#0A0A0A] text-white">
            <motion.div style={{ scale: mediaScale }} className="absolute inset-0">
                <HeroMedia url={hero.video_url} poster={hero.poster_url} className="absolute inset-0 h-full w-full object-cover" />
            </motion.div>
            <div className="absolute inset-0 bg-black/45" />
            <div className="absolute inset-x-0 bottom-0 h-48 bg-gradient-to-t from-black/70 to-transparent" />

            <motion.div style={{ y: linesY }} className="absolute -right-48 top-1/2 hidden -translate-y-1/2 md:block">
                <FieldLines className="h-[48rem] w-[48rem]" />
            </motion.div>
            <motion.span style={{ y: plusY }} aria-hidden="true" className="font-display absolute left-[6%] top-[16%] select-none text-6xl text-[#D60024] md:text-8xl">
                +
            </motion.span>

            <motion.div style={{ y: textY, scale: textScale, opacity: fade }} className="relative z-10 flex h-full origin-bottom-left flex-col justify-end px-6 pb-24 md:px-12 lg:px-24">
                <div className="mb-6 overflow-hidden">
                    <motion.p
                        initial={{ y: "120%" }}
                        animate={{ y: 0 }}
                        transition={{ duration: 1, delay: 0.2, ease: EASE }}
                        data-testid="hero-tagline"
                        className="text-xs font-semibold uppercase tracking-[0.35em] text-[#D60024] md:text-sm"
                    >
                        {tx(hero.tagline)}
                    </motion.p>
                </div>
                <h1 className="font-display leading-[0.85] tracking-tight">
                    {hero.logo_url ? (
                        <>
                            <span className="sr-only">{`${tx(hero.headline_1)} ${tx(hero.headline_2)}`}</span>
                            <span className="block overflow-hidden">
                                <motion.img
                                    src={hero.logo_url}
                                    alt="Beyond The Field+"
                                    initial={{ y: "112%" }}
                                    animate={{ y: 0 }}
                                    transition={{ duration: 1.15, delay: 0.35, ease: EASE }}
                                    data-testid="hero-logo"
                                    className="block w-[min(88vw,54rem)] select-none"
                                    draggable="false"
                                    aria-hidden="true"
                                />
                            </span>
                        </>
                    ) : (
                        <>
                            <span className="block overflow-hidden">
                                <motion.span
                                    initial={{ y: "112%" }}
                                    animate={{ y: 0 }}
                                    transition={{ duration: 1.15, delay: 0.35, ease: EASE }}
                                    data-testid="hero-headline-1"
                                    className="block text-[clamp(4.5rem,15vw,13rem)]"
                                >
                                    {tx(hero.headline_1) || "BEYOND"}
                                </motion.span>
                            </span>
                            <span className="block overflow-hidden">
                                <motion.span
                                    initial={{ y: "112%" }}
                                    animate={{ y: 0 }}
                                    transition={{ duration: 1.15, delay: 0.5, ease: EASE }}
                                    data-testid="hero-headline-2"
                                    className="block whitespace-nowrap text-[clamp(3.2rem,12.5vw,11rem)]"
                                >
                                    {h2Main}
                                    <span className="text-[#D60024]">+</span>
                                </motion.span>
                            </span>
                        </>
                    )}
                </h1>
                <div className="mt-10 flex flex-col gap-8 md:flex-row md:items-end md:justify-between">
                    <motion.p
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 1, delay: 0.85, ease: EASE }}
                        data-testid="hero-subtext"
                        className="max-w-md text-sm text-white/75 md:text-base"
                    >
                        {tx(hero.subtext)}
                    </motion.p>
                    <motion.div
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 1, delay: 1, ease: EASE }}
                        className="flex flex-wrap gap-4"
                    >
                        <PlusButton href={hero.cta_primary_url || "#manifesto"} label={tx(hero.cta_primary_label) || "DISCOVER MORE"} testId="hero-cta-primary" />
                        <PlusButton href={hero.cta_secondary_url || "#contact"} label={tx(hero.cta_secondary_label) || "START A PROJECT"} testId="hero-cta-secondary" variant="solid" />
                    </motion.div>
                </div>
            </motion.div>

            <div className="absolute bottom-6 right-6 z-10 flex items-center gap-3 text-[10px] uppercase tracking-[0.3em] text-white/60 md:right-12">
                <span>Scroll</span>
                <span className="block h-10 w-px overflow-hidden bg-white/30">
                    <motion.span className="block h-4 w-px bg-[#D60024]" animate={{ y: [0, 42] }} transition={{ repeat: Infinity, duration: 1.4, ease: "easeInOut" }} />
                </span>
            </div>
        </section>
    );
}
